import { getPropertyData } from '@/apis/apis';
import MapView from '@/components/GoogleMap/MapView';
import Header from '@/components/Header'
import useDocumentTitle from '@/components/Helmet';
import { useCustomLoader } from '@/components/Loader';
import CustomTable from '@/components/material/CustomTable';
import SearchFilters from '@/components/SearchFilters';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import API_URL from '@/config';
import { useToast } from '@/hooks/use-toast';
import axiosInstance from '@/lib/axiosInstance'
import { formatPrice, statusColors } from '@/lib/utils';
import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Avatar, Box, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography, useMediaQuery } from '@mui/material'
import { Eye, Pencil, Plus, Trash, Trash2 } from 'lucide-react';
import { it } from 'node:test';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'wouter';

interface PropertyFilters {
  category?: string;
  propertyType?: string;
  minPrice?: number;
  maxPrice?: number;
  bedrooms?: number;
  bathrooms?: number;
  city?: string;
  county?: string;
  search?: string;
  sortBy?: 'price-asc' | 'price-desc' | 'date-desc' | 'date-asc';
}

const Index = () => {
  const loader = useCustomLoader();
  useDocumentTitle("Property List");
  const { toast } = useToast();
  const isMobile = useMediaQuery("(max-width: 600px)");

  const [_, navigate] = useLocation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [properties, setProperties] = useState<any>({});
  const [filters, setFilters] = useState<PropertyFilters>({});
  const [openModal, setOpenModal] = useState<any>(null);
  const [isMapView, setIsMapView] = useState(false);
  const [expanded, setExpanded] = React.useState<string | false>(false);

  const handleClose = () => setOpenModal(null);

  const fetchProperty = useCallback(async () => {
    loader(true);
    try {
      await getPropertyData({ filters, page, rowsPerPage })
        .then((res) => {
          const data = res?.data?.map((item: any) => {
            return {
              ...item,
              active: item.active ? "Active" : "Inactive",
            }
          })
          setProperties({
            data: data,
            count: res?.count,
          });
        })
        .catch((error) => {
          toast({
            title: "Error!",
            description: error?.response?.data?.message,
          })
        })
    } catch (error: any) {
      toast({
        title: "Error!",
        description: error?.response?.data?.message,
      })
    }
    finally {
      loader(false);
    }
  }, [page, rowsPerPage, filters])

  useEffect(() => {
    fetchProperty()
  }, [fetchProperty])


  const handleFilterChange = (newFilters: PropertyFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/api/properties/${openModal?._id}`)
      fetchProperty();
      handleClose();
      toast({
        variant: "success",
        title: "Success!",
        description: "Property deleted successfully",
      })
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error!",
        description: error?.response?.data?.message || "Something went wrong",
      })
    }
  }
  const updateStatus = async (row: any) => {
    try {
      const { data: response } = await axiosInstance.put(`/api/properties/${row._id}`, {
        active: row.active === "Active" ? false : true
      });
      if (response.success) {
        toast({
          variant: "success",
          title: "Success!",
          description: "Status updated successfully",
        })
        fetchProperty();
      }
      else {
        toast({
          variant: "destructive",
          title: "Error!",
          description: response.message,
        })
      }
    } catch (error) {
    }
  }
  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant='h6'>Properties</Typography>
        <Button onClick={() => navigate("/property/add")}>
          <Plus /> Add
        </Button>
      </Box>
      <br />
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Input placeholder="Search by name" onChange={(e) => handleFilterChange({ ...filters, search: e.target.value })} />
        </Grid>
        {/* <Grid size={{ sm: 8, xs: 12 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <SearchFilters onFilterChange={handleFilterChange} filters={filters} setIsMapView={setIsMapView} isMapView={isMapView} />
        </Grid> */}
      </Grid>
      {
        isMapView && <MapView properties={properties?.data} />
      }
      {isMobile ? <Box>

        {
          properties?.data?.map((property: any, index: any) => (
            <Accordion
              expanded={expanded === property?._id}
              key={property._id}
              onChange={() => setExpanded(expanded === property?._id ? null : property?._id)}
              className='mb-2'
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel1-content"
                id="panel1-header"
                sx={{
                  "& .MuiAccordionSummary-content": {
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  },
                  backgroundColor: "#f9fafb",
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar src={property?.images?.length > 0 ? `${API_URL}/getImage/image/${property?.images[0]}` : ""} alt="" />
                  <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
                    {property.title}
                  </Typography>
                </Box>
                <Chip
                  label={property.isActive ? "Active" : "Inactive"}
                  size="small"
                  sx={{ ml: "auto", width: 100, height: 30 }}
                  color={property.isActive ? "success" : "error"}
                />
              </AccordionSummary>

              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <span >Price:</span><br />
                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{formatPrice(property.price)}</span>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <span >Category:</span><br />
                    <span className="font-semibold">{property.category || "N/A"}</span>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <span >Address:</span><br />
                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{property.address || "N/A"}</span>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <span >City:</span><br />
                    <span className="font-semibold">
                      {property.city || "N/A"}
                    </span>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <span>State:</span><br />
                    <span className="font-semibold">{property.state || "N/A"}</span>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <span>Country:</span><br />
                    <span className="font-semibold">{property.country || "N/A"}</span>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <span>Status:</span><br />
                    <span className="font-semibold">
                      <Chip
                        label={property.status}
                        size='small'
                        sx={{ width: 100, height: 30 }}
                        color={statusColors[property.status]}
                      />
                    </span>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <span>Description:</span><br />
                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{property.description || "N/A"}</span>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                      <Tooltip title="View">
                        <Eye className='text-green-600 cursor-pointer' onClick={() => navigate(`/property/${property._id}`)} />
                      </Tooltip>
                      <Tooltip title="Edit">
                        <Pencil className='text-blue-600 cursor-pointer' onClick={() => navigate(`/seller/property/edit/${property._id}`)} />
                      </Tooltip>
                      <Tooltip title="Delete">
                        <Trash className='text-red-600 cursor-pointer' onClick={() => setOpenModal(property)} />
                      </Tooltip>
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))
        }
      </Box> :
        <CustomTable
          data={properties?.data}
          headers={[
            { id: "name", label: "Name" },
            { id: "type", label: "Property Type" },
            { id: "address.line1", label: "Address Line 1" },
            { id: "address.city", label: "City" },
            { id: "address.postalCode", label: "Postal Code" },
            { id: "active", label: "Active" },
          ]}
          count={properties?.count}
          page={page}
          setPage={setPage}
          limit={rowsPerPage}
          setLimit={setRowsPerPage}
          updateStatus={updateStatus}
          actions={[
            { label: "View", icon: <Eye size={16} />, onClick: (row) => navigate(`/property/${row._id}`), color: "primary" },
            { label: "Edit", icon: <Pencil size={16} />, onClick: (row) => navigate(`/property/edit/${row._id}`), color: "secondary" },
            { label: "Delete", icon: <Trash2 size={16} />, onClick: (row) => setOpenModal(row), color: "red" },
          ]}
        />
      }



      <Dialog
        open={!!openModal}
        // slots={{
        //     transition: Transition,
        // }}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>{`Are you sure want to delete ${openModal?.title} property?`}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            This action cannot be undone
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ display: "flex", justifyContent: "space-between" }}>
          <Button onClick={handleClose} variant='error' color='primary'>Cancel</Button>
          <Button onClick={handleDelete} variant='default' color='error'>Yes, Delete It</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Index